import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Toolbar } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../Context/authContext";
import Header from "../Components/header";
import Footer from "../Components/footer";
import DisplayProject from "../Components/DisplayProject/displayProject";
import theme from "../Styles/theme";
import "../Styles/general.css";
import {
  fetchAllProjectsViewProject,
  fetchProjectViewProject,
  generateCode,
} from "../Controllers/projectController";
import { fetchUnits } from "../Controllers/unitController";
import { fetchActivities } from "../Controllers/activityController";
import { fetchProjectDecisions } from "../Controllers/decisionController";
import { useError } from "../Context/errorContext";
import { fetchAllCategories2 } from "../Controllers/categoryController";
import { statuses } from "../Components/helpers";

const ViewProject = () => {
  const { uid } = useParams();
  const { user } = useAuth();

  const [decisions, setDecisions] = useState(null);
  const [similarProjects, setSimilarProjects] = useState(null);
  const [fixedCode, setFixedCode] = useState(null);
  const [serialNumber, setSerialNumber] = useState(null);
  const [project, setProject] = useState(null);
  const [unit, setUnit] = useState(null);
  const [units, setUnits] = useState([]);
  const [activity, setActivity] = useState(null);
  const [activities, setActivities] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { error, setError, setAlert } = useError();
  const [attachments, setAttachments] = useState([]);

  const refreshProject = async () => {
    return await fetchProjectViewProject(
      uid,
      setProject,
      setUnit,
      setActivity,
      setStatus,
      setAttachments,
      setError,
    );
  };

  const setCodeFields = async (code, unit_id, activity_id) => {
    const categories = await fetchAllCategories2(unit_id, activity_id);
    const lastCategory =
      (categories ?? []).length !== 0
        ? categories.reduce((max, cat) =>
            cat.code_position > max.code_position ? cat : max,
          )
        : null;

    let codeLength =
      (lastCategory?.code_position ?? 0) + (lastCategory?.code_length ?? 0) - 1;
    if (codeLength === -1) codeLength = 3;
    const partialCode = code?.substring(0, codeLength);
    const serialNr = code?.substring(codeLength, code.length);
    setFixedCode(partialCode);
    setSerialNumber(serialNr);
    return partialCode;
  };

  const fetchAllData = async () => {
    try {
      let project = await refreshProject();
      let res2 = fetchProjectDecisions(uid, setDecisions, setError);
      let res3 = fetchUnits(setUnits);

      let res4;
      if (project && project.status_id > statuses.DRAFT) {
        let partialCode;
        if (project.status_id >= statuses.CONFIRMED) {
          partialCode = await setCodeFields(
            project.code,
            project.unit_id,
            project.activity_id,
          );
        } else if (
          user.userData.userRoles.includes("Accounting") ||
          user.userData.userRoles.includes("Administrator")
        ) {
          const code = await generateCode(uid, setAlert, setError);
          partialCode = await setCodeFields(
            code,
            project.unit_id,
            project.activity_id,
          );
        }
        res4 = fetchAllProjectsViewProject(
          partialCode,
          setSimilarProjects,
          setError,
        );
      }

      let res5 = fetchActivities(setActivities);
      await Promise.all([res2, res3, res4, res5]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [uid]);

  if (loading) {
    return (
      <Box className="mainContainer" textAlign="center">
        <CircularProgress />
        <Typography>Loading project...</Typography>
      </Box>
    );
  }

  if (error) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <Toolbar sx={{ height: "100px" }} />
      <Box className="mainContainer">
        <DisplayProject
          uid={uid}
          user={user}
          decisions={decisions}
          setDecisions={setDecisions}
          project={project}
          unit={unit}
          units={units}
          activity={activity}
          activities={activities}
          status={status}
          similarProjects={similarProjects}
          fixedCode={fixedCode}
          serialNumber={serialNumber}
          setSerialNumber={setSerialNumber}
          setCodeFields={setCodeFields}
          refreshProject={refreshProject}
          attachments={attachments}
        />
      </Box>
      <Footer />
    </ThemeProvider>
  );
};

export default ViewProject;
